import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { treks } from '../data/trekData';
import { MapPin, Clock, TrendingUp, Users, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import PriceAnimation from '@/components/ui/price-animation';
import ReactMarkdown from "react-markdown";

const TrekDetail = () => {
  const { id } = useParams();
  const trek = treks.find(t => t.id === parseInt(id));

  if (!trek) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Trek Not Found</h1>
          <Button asChild>
            <Link to="/treks">Back to All Treks</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent z-10" />
          <img
            src={trek.image}
            alt={trek.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-20 container mx-auto px-4 pb-12">
          <Button
            asChild
            variant="ghost"
            className="text-white hover:text-orange-400 mb-4"
          >
            <Link to="/treks">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Treks
            </Link>
          </Button>
          <Badge className="bg-orange-600 hover:bg-orange-700 text-white mb-4">
            {trek.difficulty}
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">{trek.name}</h1>
          <div className="flex flex-wrap items-center gap-4 text-gray-200">
            <div className="flex items-center">
              <MapPin className="h-5 w-5 mr-2" />
              {trek.location}
            </div>
            <div className="flex items-center">
              <Clock className="h-5 w-5 mr-2" />
              {trek.duration}
            </div>
            <div className="flex items-center">
              <TrendingUp className="h-5 w-5 mr-2" />
              {trek.altitude}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-4">Overview</h2>
                <div className="text-lg text-gray-700 leading-relaxed">
                  <ReactMarkdown>{trek.longDescription}</ReactMarkdown>
                </div>
              </div>

              {/* Highlights */}
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Highlights</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {trek.highlights.map((highlight, index) => (
                    <Card key={index} className="border-l-4 border-l-orange-600">
                      <CardContent className="pt-6">
                        <div className="flex items-start">
                          <CheckCircle className="h-5 w-5 text-orange-600 mr-3 flex-shrink-0 mt-0.5" />
                          <p className="text-gray-700">{highlight}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Itinerary */}
              <div>
                <h2 className="text-3xl font-bold text-slate-900 mb-6">Detailed Itinerary</h2>
                <Accordion type="single" collapsible className="space-y-4">
                  {trek.itinerary.map((day, index) => (
                    <AccordionItem key={index} value={`day-${day.day}`} className="border rounded-lg px-6 shadow-sm">
                      <AccordionTrigger className="hover:text-orange-600 hover:no-underline">
                        <div className="flex items-center">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                            <span className="font-bold text-orange-600">Day {day.day}</span>
                          </div>
                          <span className="font-semibold text-left">{day.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-2 pb-4 pl-4">
                        <div className="text-gray-700 leading-relaxed">
                        <ReactMarkdown
                          components={{
                            p: ({ children }) => <p className="mb-2">{children}</p>,
                            ul: ({ children }) => (
                              <ul className="list-disc list-outside ml-5 mb-2" style={{ listStyleType: 'disc' }}>
                                {children}
                              </ul>
                            ),
                            li: ({ children }) => <li className="mb-1">{children}</li>,
                            strong: ({ children }) => <strong className="font-semibold text-black">{children}</strong>,
                          }}
                        >
                          {day.description}
                        </ReactMarkdown>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>

              {/* Inclusions & Exclusions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Card className="border-2 border-green-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-green-700">
                      <CheckCircle className="h-6 w-6 mr-2" />
                      What's Included
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {trek.inclusions.map((item, index) => (
                        <li key={index} className="flex items-start text-gray-700">
                          <span className="text-green-600 mr-2 mt-1">✓</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-2 border-red-200">
                  <CardHeader>
                    <CardTitle className="flex items-center text-red-700">
                      <XCircle className="h-6 w-6 mr-2" />
                      What's Not Included
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {trek.exclusions.map((item, index) => (
                        <li key={index} className="flex items-start text-gray-700">
                          <span className="text-red-600 mr-2 mt-1">✕</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24 shadow-xl border-2 border-orange-100">
                <CardHeader className="bg-orange-50">
                  <div className="text-center">
                    <div className="text-sm text-gray-600 mb-2">Starting from</div>
                    <div ><PriceAnimation actual={trek.actualPrice} offer={trek.offerPrice} /> </div>
                    <div className="text-sm text-gray-600">per person</div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-gray-600">Duration</span>
                      <span className="font-semibold text-slate-900">{trek.duration}</span>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-gray-600">Difficulty</span>
                      <Badge className="bg-orange-600 hover:bg-orange-700 text-white">
                        {trek.difficulty}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between py-3 border-b">
                      <span className="text-gray-600">Max Altitude</span>
                      <span className="font-semibold text-slate-900">{trek.altitude}</span>
                    </div>
                    {/* <div className="flex items-center justify-between py-3">
                      <span className="text-gray-600">Group Size</span>
                      <span className="font-semibold text-slate-900 flex items-center">
                        <Users className="h-4 w-4 mr-1" />
                        8-15 people
                      </span>
                    </div> */}
                  </div>

                  <Button
                    asChild
                    size="lg"
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg"
                  >
                    <Link to="/contact">Book This Trek</Link>
                  </Button>

                  <Button
                    asChild
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white py-6"
                  >
                    <Link to="/contact">Request Custom Quote</Link>
                  </Button>

                  <div className="text-center text-sm text-gray-600 pt-4 border-t">
                    <p>Have questions?</p>
                    <Link to="/contact" className="text-orange-600 hover:text-orange-700 font-semibold">
                      Contact us for details
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TrekDetail;